export const settingsArticles = [
    {
        id: 'settings.overview',
        title: 'Settings Overview',
        category: 'Settings',
        order: 1,
        summary: 'Configuring application settings',
        content: `
The Settings page allows you to configure various aspects of the application for your organization.

**Settings Categories:**

- **Account** - Your profile and password
- **Organisation** - Company profile, branding, and personnel
- **Estimating Setup** - Trade groups & cost centres, units, and catalogue types
- **Accounting** - Tax, document numbering, and project defaults
- **Users & Access** - Users & roles, licence seats, feature access, and the audit log
- **Billing & Licences** - Subscription and billing
- **Modules** - RFI and timesheet configuration
- **Developer & AI** - API keys, webhooks, and AI assistant keys (admin only)
    `,
        expandableSections: [
            {
                title: 'Settings Navigation',
                content: `
Use the sidebar to navigate settings sections:

- Click a category to view its settings
- Some sections have subsections
- Changes save automatically or require Save button
        `,
            },
        ],
        keywords: ['settings', 'configuration', 'preferences', 'setup'],
        route: '/settings',
        relatedArticles: ['settings.profile', 'settings.organization', 'settings.accounting'],
        screenshots: [
            {
                src: 'settings/settings-overview.png',
                alt: 'Settings page overview',
                caption: 'Configure application settings for your organization',
            },
        ],
    },
    {
        id: 'settings.profile',
        title: 'My Profile',
        category: 'Settings',
        order: 2,
        summary: 'Managing your personal profile settings',
        content: `
Update your personal information and preferences.

**Profile Settings:**

- Name and contact details
- Email notifications
- Password change
- Theme preference
    `,
        expandableSections: [
            {
                title: 'Updating Your Profile',
                content: `
1. Go to Settings > My Profile
2. Update your details:
   - Display Name
   - Email Address
   - Phone Number
3. Click **Save**

**Change Password:**
1. Click **Change Password**
2. Enter current password
3. Enter new password twice
4. Click **Update**
        `,
            },
            {
                title: 'Notification Preferences',
                content: `
Control which email notifications you receive:

- Task assignments
- Workflow assignments
- System announcements
- Report delivery

Toggle each notification type on/off.
        `,
            },
        ],
        keywords: ['profile', 'personal', 'account', 'password', 'preferences'],
        route: '/settings',
        relatedArticles: ['settings.overview'],
    },
    {
        id: 'settings.organization',
        title: 'Organization Settings',
        category: 'Settings',
        order: 3,
        summary: 'Company profile and branding settings',
        content: `
Configure your organization's profile and branding.

**Organization Settings:**

- Company name and details
- Logo and branding
- Address information
- Contact details
    `,
        expandableSections: [
            {
                title: 'Company Profile',
                content: `
1. Go to Settings > Company Profile
2. Update company details:
   - Company Name
   - ABN
   - Address
   - Phone/Email
3. Click **Save**

This information appears on:
- Purchase orders
- Progress claims
- Reports and exports
        `,
            },
            {
                title: 'Branding',
                content: `
Customize the appearance:

1. Go to Settings > Branding
2. Upload company logo
3. Set brand colors (optional)
4. Preview on documents
5. Save changes

Logo appears on:
- Document headers
- PDF exports
- Email templates
        `,
            },
        ],
        keywords: ['organization', 'company', 'branding', 'logo', 'profile'],
        route: '/settings',
        relatedArticles: ['settings.overview'],
    },
    {
        id: 'settings.accounting',
        title: 'Accounting Settings',
        category: 'Settings',
        order: 4,
        summary: 'Financial and accounting configuration',
        content: `
Configure accounting-related settings including tax, numbering, and bank details.

**Accounting Settings:**

- GST rate
- Invoice numbering
- Payment terms
- ABA bank configuration
    `,
        expandableSections: [
            {
                title: 'Tax Settings',
                content: `
Configure GST/tax settings:

1. Go to Settings > Accounting
2. Set GST rate (default 10%)
3. Configure tax-inclusive/exclusive defaults
4. Save

These settings affect:
- Invoice calculations
- BOQ pricing
- Reports
        `,
            },
            {
                title: 'Numbering Sequences',
                content: `
Configure auto-numbering:

- **Invoice Prefix** - e.g., "INV-"
- **Order Prefix** - e.g., "PO-"
- **Next Number** - Starting number

Numbers increment automatically when creating new records.
        `,
            },
        ],
        keywords: ['accounting', 'tax', 'GST', 'numbering', 'financial'],
        route: '/settings',
        relatedArticles: ['settings.overview', 'settings.aba'],
    },
    {
        id: 'settings.aba',
        title: 'ABA Configuration',
        category: 'Settings',
        order: 5,
        summary: 'Setting up bank details for ABA file generation',
        content: `
Configure your bank details for generating ABA (direct deposit) payment files.

**Required Information:**

- Bank Name
- BSB Number
- Account Number
- Account Name
- User ID (bank-provided)
    `,
        expandableSections: [
            {
                title: 'ABA Setup',
                content: `
1. Go to Settings > Accounting > ABA Configuration
2. Enter bank details:
   - Bank Name (description)
   - BSB (6 digits)
   - Account Number
   - Account Name
3. Enter User ID:
   - Provided by your bank
   - Required for ABA processing
4. Save

Test by generating a payment batch.
        `,
            },
            {
                title: 'Multiple Bank Accounts',
                content: `
You can configure multiple bank accounts:

1. Add additional accounts
2. Set one as default
3. Choose account when creating payment batches

Useful for:
- Different payment types
- Multiple business units
        `,
            },
        ],
        keywords: ['ABA', 'bank', 'direct deposit', 'payments', 'BSB'],
        route: '/settings',
        relatedArticles: ['settings.accounting', 'accounting.payments'],
    },
    {
        id: 'settings.cost-centres',
        title: 'Cost Centres',
        category: 'Settings',
        order: 6,
        summary: 'Managing master cost centre list',
        content: `
Manage the master list of cost centres available across your organization.

**Cost Centre Management:**

- Create standard cost centres
- Set hierarchy structure
- Import from templates
- Assign default values
    `,
        expandableSections: [
            {
                title: 'Managing Cost Centres',
                content: `
1. Go to Settings > Cost Centres
2. View existing cost centres
3. Add new:
   - Click **Add**
   - Enter code and name
   - Set parent (for hierarchy)
4. Edit existing:
   - Click to edit
   - Update details
   - Save

**Import:**
Import standard cost centre lists from industry templates.
        `,
            },
        ],
        keywords: ['cost centres', 'cost centers', 'sections', 'categories'],
        route: '/settings',
        relatedArticles: ['settings.overview', 'boq.cost-centres'],
    },
    {
        id: 'settings.units',
        title: 'Units of Measure',
        category: 'Settings',
        order: 7,
        summary: 'Managing units and calculation routines',
        content: `
Configure units of measure and their calculation behavior.

**Unit Properties:**

- Code (EA, M, M2, etc.)
- Description
- Calculation Routine
- Divisor
- Rounding
    `,
        expandableSections: [
            {
                title: 'Managing Units',
                content: `
1. Go to Settings > Units
2. View existing units
3. Add new:
   - Click **Add Unit**
   - Enter code and description
   - Select calculation routine
   - Set divisor and rounding
4. Save

**Standard Units:**
System includes standard units (EA, M, M2, M3, etc.).
Add custom units as needed.
        `,
            },
            {
                title: 'Calculation Settings',
                content: `
Each unit has:

- **Routine** - How to calculate (see Calculation Routines help)
- **Divisor** - Conversion factor
- **Rounding** - Quantity rounding (0/1 = none, >1 = round to multiple)

Changing these affects all items using the unit.
        `,
            },
        ],
        keywords: ['units', 'measure', 'UOM', 'calculation'],
        route: '/settings',
        relatedArticles: ['settings.overview', 'features.calculations', 'catalogue.units'],
    },
    {
        id: 'settings.users',
        title: 'Users & Roles',
        category: 'Settings',
        order: 8,
        summary: 'Managing users, roles, permissions, and invitations',
        content: `
Manage user accounts, roles, and security settings for your organisation. Only **admin** users can access this section.

**Key Capabilities:**

- Invite new users via email with a shareable link
- Assign roles (Admin, User, Viewer, Estimator)
- Set security levels (0-5) with granular permissions
- Configure spending limits and approval thresholds
- Override feature access per-user
    `,
        expandableSections: [
            {
                title: 'Inviting Users',
                content: `
1. Go to **Settings > Users & Roles**
2. Click **Invite User** (admin only)
3. Enter:
   - Email address
   - Role (Admin, User, Viewer, Estimator)
   - Security Level (0-5)
   - Permission flags (Can Setup, Can Post)
   - Spending limits (Order, Budget, Variation)
4. Click **Send Invitation**

**What happens:**
- An invitation is created immediately with a unique link
- An email is sent in the background (non-blocking)
- You can copy the invite link directly if email delivery fails
- The user clicks the link to set up their account

**Pending Invitations:**
View all pending invitations below the user list. You can:
- Copy the invite link
- Resend the email
- Revoke the invitation
        `,
            },
            {
                title: 'Roles',
                content: `
Each user has a **tenant role** that determines their base access level:

| Role | Description |
|------|-------------|
| **Admin** | Full access to all features, settings, and user management. Cannot be restricted by Feature Access. |
| **User** | Standard access. Can create, edit, and manage records within their permissions. |
| **Viewer** | Read-only access by default. Useful for clients or stakeholders who only need to view data. |
| **Estimator** | Specialised role for estimating staff. Access tailored to BOQ and pricing workflows. |

**Note:** The role sets the default access. Use **Feature Access** settings to further customise what each role can see.
        `,
            },
            {
                title: 'Security Levels',
                content: `
Each user has a **security level** (0-5) that controls what actions they can perform:

| Level | Name | Description |
|-------|------|-------------|
| 0 | Viewer | View-only access. Cannot create, edit, or delete records. Can view reports. |
| 1 | Basic User | Can create and edit own records. Cannot delete. Cannot access settings. |
| 2 | Standard User | Full read/write access. Can delete own records. Cannot access admin settings. |
| 3 | Manager | Full access to all records. Can approve orders within limit. Can view audit log. |
| 4 | Administrator | Full access including settings and user management. Can approve all orders. |
| 5 | Super Admin | Unrestricted access including billing, integrations, and all system settings. |
        `,
            },
            {
                title: 'Permission Flags & Limits',
                content: `
**Permission Flags:**

| Flag | Description |
|------|-------------|
| **Can Setup** | Modify system settings, catalogue items, units, cost centres, and configuration |
| **Can Post** | Post/finalise accounting transactions, approve invoices and payments |
| **Approval Email** | Receive email notifications when orders or claims require approval |

**Spending Limits:**

| Limit | Description |
|-------|-------------|
| **Order Limit** | Maximum value for a single purchase order this user can create or approve |
| **Budget Limit** | Maximum total spending against a cost centre before requiring approval |
| **Variation Limit** | Maximum value of a variation/change order this user can approve |

Set limits to $0 for no limit (unlimited).
        `,
            },
            {
                title: 'Editing a User',
                content: `
1. Click on a user row in the grid
2. The edit modal opens with:
   - Display Name, Email
   - Security Level
   - Permission flags (Can Setup, Can Post, Approval Email)
   - Spending limits
   - Feature Access Overrides (optional)
3. Make changes and click **Save Changes**

**Feature Access Overrides:**
In the edit modal, expand **Feature Access Overrides** to customise which pages and tabs this specific user can see. Overrides take priority over the role defaults configured in Feature Access settings.

When an override differs from the role default, a reset icon appears to clear it back to the default.
        `,
            },
            {
                title: 'Audit Logs',
                content: `
View user activity:

1. Go to Settings > Audit Log
2. Filter by:
   - User
   - Action type
   - Date range
3. View detailed activity log

Tracks logins, data changes, and settings modifications.
        `,
            },
        ],
        keywords: ['users', 'security', 'roles', 'permissions', 'invite', 'access', 'security level', 'admin'],
        route: '/settings',
        relatedArticles: ['settings.overview', 'settings.feature-access'],
    },
    {
        id: 'settings.billing',
        title: 'Billing & Subscription',
        category: 'Settings',
        order: 20,
        summary: 'Manage your subscription, payment method, invoices and storage add-on',
        content: `
**Settings → Billing** shows your current plan, subscription status, and storage usage.

**Open Billing Portal** takes you to the secure Stripe billing portal (in the same tab) where you can update the payment method, download invoices, and change plans. Use the portal's return link to come straight back to this page.

If your workspace doesn't have Stripe billing set up yet (for example a trial or complimentary workspace), the button shows a message instead — the portal becomes available once a paid subscription exists.
    `,
        relatedArticles: ['settings.overview', 'settings.users'],
    },
    {
        id: 'settings.feature-access',
        title: 'Feature Access',
        category: 'Settings',
        order: 9,
        summary: 'Controlling which pages and tabs each role can see',
        content: `
Feature Access lets admins control which pages and accounting tabs are visible to each user role. This is a two-layer system: **role defaults** plus optional **per-user overrides**.

**How It Works:**

1. Configure what each role (Viewer, User, Estimator) can see by default
2. Optionally override specific users to grant or revoke access beyond their role
3. Admin role always has full access and cannot be restricted

**Backwards Compatible:** If no Feature Access config is set, all users see all pages (the original behaviour).
    `,
        expandableSections: [
            {
                title: 'Configuring Role Defaults',
                content: `
1. Go to **Settings > Feature Access**
2. Select a role tab: **Viewer**, **User**, or **Estimator**
3. Toggle pages on/off under **Sidebar Pages**:
   - Green = Visible
   - Red = Hidden
   - Use **All On** / **All Off** for quick bulk changes
4. Toggle accounting tabs on/off under **Accounting Tabs**
5. Click **Save Changes**

**What each toggle controls:**
- **Sidebar Pages** - Whether the page appears in the left navigation menu
- **Accounting Tabs** - Which tabs appear within the Accounting page

**Example:** Set Viewer role to only see Dashboard, Jobs, and Reports. Hide Accounting, Orders, and Settings from viewers entirely.
        `,
            },
            {
                title: 'Per-User Overrides',
                content: `
To customise access for a specific user beyond their role defaults:

1. Go to **Settings > Users & Roles**
2. Click on the user to edit
3. Expand **Feature Access Overrides**
4. Toggle individual pages or tabs:
   - The toggle shows the effective access (role default + override)
   - When you change a setting, it becomes an override (highlighted)
   - Click the **reset icon** (X) to remove an override and revert to the role default
5. Click **Save Changes**

**Use Cases:**
- Grant a specific viewer access to Accounting
- Hide sensitive pages from a particular user
- Give an estimator access to Settings temporarily

**Note:** Overrides are stored per-user and only contain the changed values. Everything else inherits from the role default.
        `,
            },
            {
                title: 'How Access Is Enforced',
                content: `
Feature Access is enforced at three levels:

1. **Sidebar** - Hidden pages don't appear in the navigation menu
2. **Tabs** - Hidden accounting tabs are filtered from the tab bar
3. **Route Guard** - If a user navigates directly to a hidden page URL, they are redirected to the Dashboard

**Important Notes:**
- Admin users always see everything regardless of Feature Access settings
- If no Feature Access is configured, all users see all pages (backwards compatible)
- Changes take effect immediately on the user's next page load
- Feature Access controls visibility only, not data-level permissions
        `,
            },
        ],
        keywords: ['feature access', 'visibility', 'show', 'hide', 'pages', 'tabs', 'role', 'permissions', 'restrict'],
        route: '/settings',
        relatedArticles: ['settings.users', 'settings.overview'],
    },
    {
        id: 'settings.rfi-configuration',
        title: 'RFI Configuration',
        category: 'Settings',
        order: 10,
        summary: 'Customise RFI types, statuses, stages, and priorities',
        content: `
The RFI Configuration section lets you customise the options available when creating and managing Requests for Information.

**Configurable Lists:**

- **Types** — Categorise RFIs (e.g. Error, Warning, Info, Issue, Request, Solution, Remark)
- **Statuses** — Workflow states (e.g. Draft, Open, In Progress, Resolved, Closed)
- **Stages** — Project stages (e.g. Schematic Design, Construction Documents, Construction)
- **Priorities** — Urgency levels (e.g. Low, Medium, High, Critical, Urgent)
    `,
        expandableSections: [
            {
                title: 'Customising Lists',
                content: `
For each list (Types, Statuses, Stages, Priorities):

1. Go to **Settings > RFI Configuration**
2. Select the tab for the list you want to edit
3. **Add** — Click the Add button and enter code, label, and colour
4. **Edit** — Click on an existing item to change its label or colour
5. **Remove** — Click the delete icon to remove an item
6. **Set Default** — Mark one item as the default selection for new RFIs
7. **Reset** — Click Reset to Defaults to restore the standard list

**Tips:**
- Codes should be short, lowercase identifiers (e.g. \`error\`, \`high\`)
- Labels are what users see in dropdowns
- Colours are used in status badges and filters
- Removing a status does not affect existing RFIs that use it
        `,
            },
        ],
        keywords: ['rfi settings', 'rfi configuration', 'types', 'statuses', 'stages', 'priorities', 'customise'],
        route: '/settings',
        relatedArticles: ['rfi.overview', 'settings.overview'],
    },
    {
        id: 'settings.billing',
        title: 'Billing & Licences',
        category: 'Settings',
        order: 11,
        summary: 'Manage your subscription, billing, and workspace users',
        content: `
The Billing section lets you manage your Costed.app subscription and workspace user licences.

**Sections:**

- **Billing** — View and manage your subscription plan, payment method, and billing history
- **Workspace Users** — See who has access to your workspace and manage user seats
    `,
        expandableSections: [
            {
                title: 'Subscription Management',
                content: `
1. Go to **Settings > Billing**
2. View your current plan and status
3. **Upgrade/Downgrade** — Change your subscription tier
4. **Payment Method** — Update your credit card or payment details
5. **Billing History** — View past invoices and receipts

Plan changes take effect immediately with prorated billing.
        `,
            },
            {
                title: 'Workspace Users',
                content: `
1. Go to **Settings > Workspace Users**
2. View all users with access to this workspace
3. See each user's role, last active date, and licence status

User seats are counted against your subscription plan. If you need more seats, upgrade your plan or contact support.
        `,
            },
        ],
        keywords: ['billing', 'subscription', 'plan', 'licence', 'license', 'payment', 'workspace users'],
        route: '/settings',
        relatedArticles: ['settings.overview', 'settings.users'],
    },
    {
        id: 'settings.developer',
        title: 'Developer & Public API',
        category: 'Settings',
        order: 12,
        summary: 'API keys, scopes, webhooks and the public REST API',
        content: `
**Settings → Developer & AI → API & Webhooks** (admin only) is where you connect
external tools to Costed. It gives you a versioned, tenant-scoped public REST API,
self-service API keys, and outbound webhooks — all isolated to your own workspace.
(The neighbouring **AI Keys & Assistant** panel is where you add your own AI provider
keys for invoice extraction and the Ask Costed assistant.)

**The public API** lets other applications read your data over HTTPS: jobs, catalogue
items, suppliers, cost centres, contacts and units, plus token-based public quote / RFQ
/ RFI views. The full, interactive reference is at the public **Developer Portal**
(\`/developers\`), and the machine-readable OpenAPI 3.1 spec is at
\`/api/v1/openapi.json\`. The data endpoints are **read-only today** — writes are
coming in a later release.

**API keys** authenticate those calls. Create a key in the Developer panel, give it a
name, then copy the key — it starts with \`costed_sk_\` and is **shown only once**, so
store it somewhere safe. Send it as a bearer token:

\`\`\`
Authorization: Bearer costed_sk_...
\`\`\`

You can revoke a key at any time, and the **Recent API activity** log shows every call
each key has made (method, path, status and timing).
    `,
        expandableSections: [
            {
                title: 'Read-only vs read & write, and per-domain scopes',
                content: `
When you create a key you choose **Read only** or **Read & write** as its overall
access level. (Write endpoints aren't live yet, so in practice the API is read-only
today — read & write is reserved for when data writes ship.)

By default a key has **full access** to every domain. To lock a key down, switch it to
**Limit to specific domains** and set each domain — jobs, catalogue, suppliers, cost
centres, contacts, units, orders, bill of quantities, selections, usage — to **No
access**, **Read**, or **Read & write**. Once you
restrict a key it becomes an allowlist: any domain you didn't grant is denied with a
403. Clear all the restrictions to return the key to full access.
        `,
            },
            {
                title: 'Webhooks — get notified when things change',
                content: `
Instead of polling the API, register a **webhook** so Costed pushes events to your
HTTPS endpoint as they happen. Add a webhook with your endpoint URL and tick the events
you care about:

- **Jobs** — job.created, job.updated
- **Orders** — order.created, order.status_changed
- **Catalogue** — catalogue_item.created, catalogue_item.price_updated
- **RFQs** — rfq.created, rfq.response_received

On creation you're shown a **signing secret once** — save it. Every delivery includes an
\`X-Costed-Signature: sha256=...\` header (an HMAC-SHA256 of the payload using your
secret) plus \`X-Costed-Timestamp\`; verify it to confirm the request really came from
Costed. Use **Send test** to fire a sample event, **pause/resume** or **edit events**
any time, and open the **delivery log** to see recent attempts and their status
(delivered, failed, or dead-lettered after repeated failures). Your endpoint must be
public HTTPS — private or localhost URLs are rejected.
        `,
            },
        ],
        keywords: [
            'developer', 'api', 'api key', 'apikey', 'rest api', 'public api', 'webhook', 'webhooks',
            'openapi', 'integration', 'integrate', 'scopes', 'costed_sk_', 'bearer token', 'signing secret',
        ],
        route: '/settings',
        relatedArticles: ['settings.overview', 'settings.users'],
    },
];
